import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Role-based module gating.
 * - "admin" role has access to every module.
 * - "staff" (or legacy "hr" / "store_manager") → team-room / presently / petty-cash
 * - "sales_team" | "management" | "dispatch_team" → quotes
 * Users with the wrong role for a module are bounced to that module's login page.
 */
const TEAM_ROLES = new Set(["admin", "staff", "hr", "store_manager"]);
const ACCESS = {
  "team-room": TEAM_ROLES,
  presently: TEAM_ROLES,        // legacy URL still allowed for staff/admin
  "petty-cash": TEAM_ROLES,     // legacy URL still allowed for staff/admin
  quotes: new Set(["admin", "sales_team", "management", "dispatch_team"]),
};

// When a wrong-role user hits a legacy /presently or /petty-cash URL, bounce
// them to the new unified /workspace login instead of the old per-module one.
const LOGIN_URL = {
  "team-room": "/workspace/login",
  presently: "/workspace/login",
  "petty-cash": "/workspace/login",
  quotes: "/quotes/login",
};

export default function ModuleGuard({ module, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return null;
  const loginUrl = LOGIN_URL[module] || `/${module}/login`;
  if (!user) {
    return <Navigate to={loginUrl} state={{ from: location.pathname }} replace />;
  }
  const allowed = ACCESS[module] || new Set();
  if (!allowed.has(user.role)) {
    return <Navigate to={loginUrl} state={{ wrongRole: true }} replace />;
  }
  return children;
}
